import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { CalculatedOdds } from '@frontend/sports/odds/feature';

import { BetslipType } from '../../core/betslip-type';
import { BetslipPick } from '../../core/picks/betslip-pick';
import { PickId } from '../../core/picks/pick-id';
import { IBasePrice, IPlaceTerms, PriceType } from '../../core/picks/pick-models';
import { IRewardToken } from '../reward-tokens/reward-tokens.model';
import { ComboPreventionResult } from '../validation/services/validators/combo-prevention/combo-prevention-result.enum';
import { SlipType } from './slip-type';

export enum BetType {
    Normal, // Normal bet
    EachWay, // EachWay bet.
    Combinational, // Bet with Combinational participant leg, betCount > 1
}

export interface ICalculatedPrice {
    type: PriceType;
    odds: CalculatedOdds;
}

export interface IBetLeg {
    pickId: PickId;
    price: ICalculatedPrice; // Leg price
}

/**
 * Represents a user bet.
 * If legs are set as Each Way contains Regular and the EachWay price
 * If legs are forecast or 3cast represents a combination with the other legs
 *
 * @example a combinational forecast contains:
 * Participant One can be 1st or 2nd place,
 * Participant Two can be 1st or 2nd place.
 * Combining this leg with another single leg (EW is not allowed) will create following bets
 * 1.
 * 	- Leg 1
 *  - Participant One to be 1st
 *  - Participant Two to be 2nd
 * 2.
 *  - Leg 1
 *  - Participant One to be 2nd
 *  - Participant Two to be 1st
 * This are two bets, but we count the forecast as one leg and the bet count is set to 2. If we combine multiple forecast legs we multiply their bet count.
 */
export interface IBet {
    legs: IBetLeg[];
    betCount: number; // 2cast and 3cast participant legs create multiple bets for different participants, but because the leg is one we will have multiple duplications, so we just write here the bet count.
    betType: BetType;
    index?: number;
    isValid: boolean;
}

export interface ISlipPick<T = BetslipPick> {
    pick: T;
    price: IBasePrice;
    eachWayInfo: EachWayInfo | null; // Is pick set as Each Way.
    isBanker: boolean; // Is this pick is represented in every combination, e.g. banker picks
    priceIds: number[]; // The list of prices selected.
    isXCast: boolean;
    isStartingPrice: boolean;
}

/**
 * Represents a list of bets created from same list of picks combined in particular way
 */
export interface ISlip {
    slipId: SlipId;
    slipPicks: Record<string, ISlipPick>;
    index: number;
    bets: IBet[];
    type: SlipType;
    stake: number | null; // Stake is null when input field is empty, all bets in the slip share the same stake.
    rewardToken: Nullable<IRewardToken>; // A reward token can be applied per slip.
    hasError: boolean;
}

export interface EachWayInfo {
    placeTerms: IPlaceTerms;
}

export interface PickInfo {
    pick: BetslipPick;
    price: IBasePrice | null;
    eachWayInfo: EachWayInfo | null; // Is pick set as Each Way.
    isBanker: boolean; // Is this pick is represented in every combination, e.g. banker picks
    priceIds: number[]; // The list of prices selected.
    isXCast: boolean;
    isStartingPrice: boolean;
}

// Define a bet override, Bet is defined as combination of SystemType and list of pickIds.
export interface IBetslipCalculatorSlipOverride {
    system: SlipType;
    pickIds: string[]; // For now this will be used only for single bets, but in future it is possible to override a stake of a specific system bet.
    stake?: {
        // Stake override for a bet, used in the keypad.
        value: number | null;
    };
    rewardToken?: {
        // Reward token override.
        token: IRewardToken | null; // if reward token is set to NULL then token is removed, for this bet.
    };
}

export interface IBetslipCalculatorPicksOverride {
    replacements?: {
        // Define pick replacements, if we replace one pick with another: Line switcher, Edit Bet: Swap.
        [pickId: string]: PickInfo | null; // When set to null, pick will not participate in the calculations.
    };
    additions?: {
        [pickId: string]: PickInfo;
    };
    prices?: {
        // Define price overrides.
        [pickId: string]: IBasePrice;
    };
    eachWay?: {
        // Define EachWay overrides for a pick.
        [pickId: string]: EachWayInfo | null; // If null EachWay is removed, when has value use its place terms.
    };
}

export interface IBetslipCalculatorOverrides {
    slipOverrides: IBetslipCalculatorSlipOverride[];
    pickOverrides: IBetslipCalculatorPicksOverride;
}

export const emptyBetslipCalculatorOverrides: IBetslipCalculatorOverrides = Object.freeze({
    slipOverrides: Object.freeze([] as IBetslipCalculatorSlipOverride[]) as IBetslipCalculatorSlipOverride[],
    pickOverrides: Object.freeze({
        replacements: Object.freeze({}),
        prices: Object.freeze({}),
        eachWay: Object.freeze({}),
    }),
});

export type SlipId = string;

export interface ICalculatorBetslipInput {
    slipId: SlipId;
    system: SlipType;
    items: PickInfo[];
    stake: number | null;
    rewardToken: IRewardToken | null; // Slip reward token.
    hasError?: boolean;
}

export interface ICalculatorInput {
    betslip: ICalculatorBetslipInput[];
}

export interface ILinearCalculatorInput {
    typeInputs: ILinearTypeCalculatorInput[];
}

export interface ILinearTypeCalculatorInput {
    type: BetslipType;
    input: ICalculatorInput;
}

export interface IBetslip {
    slips: ISlip[];
}

export interface ITypeBetslip {
    slips: ISlip[];
    type: BetslipType;
}

export interface IFullBetslip {
    typeBetslips: Partial<Record<BetslipType, ITypeBetslip>>;
}

// Basically we can check everything by state where we are but it is better the component to pass directly the context.
export enum BetslipGeneratorContextType {
    Betslip, // BetStation, QuickBet, EditBet
    EditBetSwap, // Edit bet in swap mode
    BetslipLineSwitcher, // MS2 betslip with line switcher
    OverAsk,
    MultiReturnMessage,
    AccaBoost,
    BetslipBar,
    BetBuilder,
}

/**
 * This object contains bet combination restrictions
 */
export interface CombinationRestrictions {
    /**
     * Information about not combinable picks: Dictionary with a key pickId and value map of pickIds this pick has problems
     */
    notCombinablePicks: Record<string, Record<string, ComboPreventionResult>>;
    /**
     * Minimum combo restrictions.key = pick id, value : minimum picks in combo required.
     */
    minimumCombinations: Record<string, number>;
}

export const emptyCombinationRestrictions: CombinationRestrictions = {
    notCombinablePicks: {},
    minimumCombinations: {},
};
